import { useMemo } from "react";

import * as appIcons from "@assets/applications";
import { AnnouncementMessages, UserRightsMessages } from "@languages/interfaces";
import { useStaffPermissions } from "@hooks/useStaffPermissions";
// LEGACY MIGRATION (temporary) — remove with pages/LegacyMigration.
import { LegacyMigrationMessages } from "@pages/LegacyMigration/languages/interfaces";
import { ERoutes } from "src/helpers/utils";

export interface MenuItem {
  id: string;
  title: string;
  icon: string;
  link: string;
  end?: boolean;
}

/**
 * Builds the sidebar navigation items for the current user. The
 * tenant-selection entry is included only for admin accounts and the
 * notifications entry only for staff holding the manage permission (the same
 * rule the page and the server enforce). Shared by the desktop `SidebarMenus`
 * and the mobile `SidebarDrawerMenus` so both surfaces stay in sync.
 */
export const useSidebarMenuItems = (): MenuItem[] => {

  const { isAdmin, canManageAnnouncements, canRunLegacyMigration } = useStaffPermissions();

  return useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        id: "home",
        title: UserRightsMessages.launcher,
        icon: appIcons.home,
        link: `/${ERoutes.identity}`,
        end: true
      },
      {
        id: "users",
        title: UserRightsMessages.rightsControl,
        icon: appIcons.userRights,
        link: "users"
      }
    ];

    if (isAdmin) {
      items.push({
        id: "select-tenant",
        title: UserRightsMessages.selectTenant,
        icon: appIcons.tenantKey,
        link: "select-tenant"
      });
    }

    // LEGACY MIGRATION (temporary) — same rule as the page and the server.
    if (canRunLegacyMigration) {
      items.push({
        id: "legacy-migration",
        title: LegacyMigrationMessages.legacyMigrationTitle,
        icon: appIcons.legacyMigration,
        link: "legacy-migration"
      });
    }

    if (canManageAnnouncements) {
      items.push({
        id: "notifications",
        title: AnnouncementMessages.menuTitle,
        icon: appIcons.notifications,
        link: "notifications"
      });
    }

    return items;
  }, [isAdmin, canManageAnnouncements, canRunLegacyMigration]);
};
